import classNames from "classnames";

import { LISCaseList, LISSlide } from "pwapi";

import { StainList } from "custom-models/frontLoader";

import { useRequestAfterEverySiteLoad } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { listCases } from "app/actions/caseActions";
import { scanSlide } from "app/actions/slideActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { workWithTaskIn } from "app/selectors/workSelectors";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";
import AppSpinner from "ui/components/AppSpinner";
import CaseListItem from "ui/lists/CaseListItem";
import { labelForSlide } from "util/slideUtils";
import { blockSort, slideSort, specimenSort } from "util/sortFunctions";

const Slide = ({
  slide,
  pathCase,
}: {
  slide: LISSlide;
  pathCase: LISCaseList;
}) => {
  const dispatch = useAppDispatch();
  const selectedLabStation = useSelectedLabStation();
  const stain: StainList | undefined = useAppSelector(
    ({ stains }) => stains[slide.stain_id || 0]
  );
  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  const onClick = (e: any) => {
    if (lisAltClickScanning && e.altKey && slide.id) {
      dispatch(
        scanSlide({
          parentLookupLabStationId: selectedLabStation.id,
          id: slide.id,
        })
      );
    }
  };

  return (
    <article
      className={classNames(
        "WorkProcessScanningSlide",
        `WorkProcessScanningSlide--${slide.lis_state}`
      )}
    >
      <p className="Pretitle">
        <code>{slide.lis_state}</code>
      </p>
      <h3 className="Scannable" onClick={onClick}>
        {labelForSlide(slide, pathCase, stain)}
      </h3>
    </article>
  );
};

const Case = ({ pathCase }: { pathCase: LISCaseList }) => {
  if (!pathCase.id) return null;

  const slides = pathCase.slides.filter((s) => s.lis_state !== "scanned");

  return (
    <CaseListItem
      id={pathCase.id}
      label={`${slides.length} Slide${slides.length === 1 ? "" : "s"}`}
    >
      {[...pathCase.specimens].sort(specimenSort).map((sp) =>
        pathCase.blocks
          .filter((b) => b.specimen_id === sp.id)
          .sort(blockSort)
          .map((b) =>
            slides
              .filter((s) => s.block_obj_id === b.id)
              .sort(slideSort)
              .map((s) => <Slide slide={s} key={s.id} pathCase={pathCase} />)
          )
      )}
    </CaseListItem>
  );
};

const ScanningStationList = () => {
  const dispatch = useAppDispatch();
  const selectedLabStation = useSelectedLabStation();
  const casesWithStainedSlides = useAppSelector(
    workWithTaskIn(["staining", "scanning"])
  );

  useBarcodeScanner(({ object_type, object_id }) => {
    if (object_type === "slide") {
      dispatch(
        scanSlide({
          parentLookupLabStationId: selectedLabStation.id,
          id: object_id,
        })
      );
    } else {
      return false;
    }
  });

  const casesReady = useRequestAfterEverySiteLoad(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
  });

  if (!casesReady) return <AppSpinner>Loading Cases...</AppSpinner>;

  return (
    <div className="Station">
      <main>
        <header className="Header">
          <figure className="Logo">
            <img src="/logo.svg" alt="" />
          </figure>
          <div>
            <p className="Label">{selectedLabStation.name}</p>
          </div>
        </header>

        <div className="WorkList">
          {casesWithStainedSlides.map((c) => (
            <Case pathCase={c} key={c.id} />
          ))}
        </div>
      </main>
    </div>
  );
};

export default ScanningStationList;
